import React from 'react';
import { Toaster } from 'sonner';
import { Loader2 } from 'lucide-react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AppProvider, useApp } from './contexts/AppContext';
import AppLayout from './app-screens/AppLayout';
// Auth pages stay eager — they're the first thing an unauthenticated visitor
// needs and are small. Everything under /app/* is code-split via React.lazy
// (item 6 do Lote 6 — tira recharts, xlsx e o kit de telas do bundle inicial;
// o wrap() abaixo já embrulha cada rota num React.Suspense).
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ForcePasswordChangePage from './pages/ForcePasswordChangePage';
import './App.css';

const Dashboard = React.lazy(() => import('./app-screens/Dashboard'));
// Screens.jsx is one large multi-export file (puxa xlsx + o kit de telas) —
// cada tela vira seu próprio ponto de entrada lazy pro mesmo chunk.
const Unconfigured = React.lazy(() => import('./app-screens/Screens').then((m) => ({ default: m.Unconfigured })));
const Configured = React.lazy(() => import('./app-screens/Screens').then((m) => ({ default: m.Configured })));
const Tasks = React.lazy(() => import('./app-screens/Screens').then((m) => ({ default: m.Tasks })));
const Settings = React.lazy(() => import('./app-screens/Screens').then((m) => ({ default: m.Settings })));
const AutoTaskMonitorPage = React.lazy(() => import('./app-screens/AutoTaskMonitorPage'));
const Graphs = React.lazy(() => import('./app-screens/GraphsPage'));
const Diagnostics = React.lazy(() => import('./app-screens/DiagnosticsPage'));
const EventsPage = React.lazy(() => import('./app-screens/EventsPage'));
const BulkActions = React.lazy(() => import('./app-screens/BulkActionsPage'));
const ExternalChanges = React.lazy(() => import('./app-screens/ExternalChangesPage'));
const ReportsHomePage = React.lazy(() => import('./app-screens/ReportsPages').then((m) => ({ default: m.ReportsHomePage })));
const ReportsInventoryPage = React.lazy(() => import('./app-screens/ReportsPages').then((m) => ({ default: m.ReportsInventoryPage })));
const ReportsSignalQualityPage = React.lazy(() => import('./app-screens/ReportsPages').then((m) => ({ default: m.ReportsSignalQualityPage })));
const ReportsTrafficSummaryPage = React.lazy(() => import('./app-screens/ReportsPages').then((m) => ({ default: m.ReportsTrafficSummaryPage })));
const ReportsPonOutagesPage = React.lazy(() => import('./app-screens/ReportsPages').then((m) => ({ default: m.ReportsPonOutagesPage })));
const ReportsOltOverviewPage = React.lazy(() => import('./app-screens/ReportsPages').then((m) => ({ default: m.ReportsOltOverviewPage })));
const ReportsPollHistoryPage = React.lazy(() => import('./app-screens/ReportsPages').then((m) => ({ default: m.ReportsPollHistoryPage })));
const OnuPage = React.lazy(() => import('./app-screens/OnuPage'));
const OnuDashboardPage = React.lazy(() => import('./app-screens/OnuDashboardPage'));
const OltDetailPage = React.lazy(() => import('./app-screens/OltDetailPage'));
const VlanEditPage = React.lazy(() => import('./app-screens/VlanEditPage'));
const PonDetailPage = React.lazy(() => import('./app-screens/PonDetailPage'));

// Lazy — pulls in Leaflet + react-leaflet + markercluster only when the
// geographic map report is actually opened (docs/DocsRelatorios/02-mapa-geografico.md).
const ReportsNetworkMapPage = React.lazy(() => import('./app-screens/ReportsMapPages'));
const ReportsCTOOccupancyPage = React.lazy(
  () => import('./app-screens/ReportsMapPages').then((m) => ({ default: m.ReportsCTOOccupancyPage })),
);
// Lazy — the OLT topology tree/diagram report (docs/DocsRelatorios/04-topologia-olt.md).
const ReportsTopologyPage = React.lazy(() => import('./app-screens/ReportsTopologyPage'));
// Lazy — PON-port capacity table + matrix reports (docs/DocsRelatorios/05-portas-pon.md).
const ReportsPonCapacityPage = React.lazy(() => import('./app-screens/ReportsPonPages'));
const ReportsPonDiagramPage = React.lazy(
  () => import('./app-screens/ReportsPonPages').then((m) => ({ default: m.ReportsPonDiagramPage })),
);
// Lazy — ONUs por tipo/fabricante + Clientes por plano de velocidade (docs/DocsRelatorios/06-onus-tipo-e-planos.md).
const ReportsOnuModelsPage = React.lazy(() => import('./app-screens/ReportsInventoryDistPages'));
const ReportsSpeedPlansPage = React.lazy(
  () => import('./app-screens/ReportsInventoryDistPages').then((m) => ({ default: m.ReportsSpeedPlansPage })),
);
// Lazy — histórico de trocas de ONU (docs/DocsRelatorios/07-trocas-de-onu.md).
const ReportsOnuSwapsPage = React.lazy(() => import('./app-screens/ReportsOnuSwapsPage'));
// Lazy — saúde de hardware da frota (docs/DocsRelatorios/08-saude-hardware.md).
const ReportsHardwareHealthPage = React.lazy(() => import('./app-screens/ReportsHardwareHealthPage'));
// Lazy — disponibilidade / uptime da frota (docs/DocsRelatorios/09-uptime-sla.md).
const ReportsUptimePage = React.lazy(() => import('./app-screens/ReportsUptimePage'));
// Lazy — consumo por cliente / fair use (docs/DocsRelatorios/10-consumo-cliente.md).
const ReportsCustomerConsumptionPage = React.lazy(() => import('./app-screens/ReportsCustomerConsumptionPage'));
// Lazy — usuários e permissões / RBAC (docs/DocsRelatorios/11-rbac.md).
const ReportsRbacPage = React.lazy(() => import('./app-screens/ReportsRbacPage'));
// Lazy — log de tarefas em lote (docs/DocsRelatorios/12-tarefas-lote.md).
const ReportsBulkJobsPage = React.lazy(() => import('./app-screens/ReportsBulkJobsPage'));
// Lazy — conformidade & backups da frota (docs/DocsRelatorios/13-conformidade-backups.md).
const ReportsBackupsPage = React.lazy(() => import('./app-screens/ReportsBackupsPage'));
// Lazy — financeiro: faturamento por OLT + pagamentos (docs/DocsRelatorios/14-financeiro.md).
const ReportsBillingOltsPage = React.lazy(() => import('./app-screens/ReportsFinancePages'));
const ReportsPaymentsPage = React.lazy(
  () => import('./app-screens/ReportsFinancePages').then((m) => ({ default: m.ReportsPaymentsPage })),
);
// Lazy — governança: uso de integrações / MCP (docs/ApiMcpOLTm/07-*).
const ReportsMcpUsagePage = React.lazy(() => import('./app-screens/ReportsMcpUsagePage'));
// Lazy — governança: comandos das OLTs / auditoria de CLI (docs/revisao2509/06-*).
const ReportsCliAuditPage = React.lazy(() => import('./app-screens/ReportsCliAuditPage'));

// Protected route: redirects to /login if not authenticated, and to
// /force-password-change if the user's must_change_password flag is set
// (owner seed / senha definida por admin — Lote2-Enforcement item 4) —
// bloqueia o resto do app até a troca, não só mostra um aviso.
function ProtectedRoute({ children }) {
  const { user } = useApp();
  const location = useLocation();
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  if (user.must_change_password && location.pathname !== '/force-password-change') {
    return <Navigate to="/force-password-change" replace />;
  }
  return children;
}

// Guest route: redirects to /app if already authenticated
function GuestRoute({ children }) {
  const { user } = useApp();
  if (user) {
    return <Navigate to="/app" replace />;
  }
  return children;
}

// Ordem canônica de tentativa (primeira rota que o user tem permissão para acessar)
const ROUTE_FALLBACK_ORDER = [
  { path: '/app', perm: 'dashboard:read' },
  { path: '/app/configured', perm: 'onus:read' },
  { path: '/app/tasks', perm: 'tasks:read' },
  { path: '/app/diagnostics', perm: 'alerts:read' },
  { path: '/app/settings/users', perm: 'users:read' },
  { path: '/app/settings', perm: 'settings:read' },
];

function firstAllowedRoute(hasPermission) {
  for (const r of ROUTE_FALLBACK_ORDER) {
    if (hasPermission(r.perm)) return r.path;
  }
  return null;
}

function FullPageSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg-primary,#0b1220)]">
      <Loader2 size={32} className="animate-spin text-sky-500" />
    </div>
  );
}

// Route + permission guard
function PermissionRoute({ perm, children }) {
  const { hasPermission, permissionsLoading } = useApp();
  // Enquanto permissões ainda não vieram do backend, mostra spinner — NUNCA
  // libera o conteúdo (fail-closed): era `if (permissions == null) return children`.
  if (permissionsLoading) return <FullPageSpinner />;
  if (!hasPermission(perm)) {
    const fallback = firstAllowedRoute(hasPermission);
    if (!fallback) {
      // Usuário não tem NENHUMA permissão → tela de sem acesso
      return (
        <div className="min-h-screen flex items-center justify-center bg-[var(--bg-primary,#0b1220)] text-white p-8">
          <div className="max-w-md text-center space-y-4">
            <div className="text-6xl">🔒</div>
            <h1 className="text-2xl font-bold">Acesso negado</h1>
            <p className="text-sm opacity-70">
              Seu usuário não tem permissão para visualizar nenhuma área desta aplicação.
              Peça ao administrador para associar seu perfil a um grupo com permissões.
            </p>
            <button
              onClick={() => { localStorage.clear(); window.location.href = '/login'; }}
              className="mt-4 px-4 py-2 rounded-full bg-sky-500 hover:bg-sky-600 text-white text-sm font-bold"
              data-testid="no-perm-logout"
            >
              Sair
            </button>
          </div>
        </div>
      );
    }
    return <Navigate to={fallback} replace />;
  }
  return children;
}

const wrap = (Component, perm) => (
  <ProtectedRoute>
    <PermissionRoute perm={perm}>
      <AppLayout>
        <React.Suspense fallback={<div className="p-10 text-center text-sm text-[var(--text-muted)]">Carregando…</div>}>
          <Component />
        </React.Suspense>
      </AppLayout>
    </PermissionRoute>
  </ProtectedRoute>
);

function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <Routes>
          {/* Auth pages — login é a página principal */}
          <Route path="/" element={<GuestRoute><LoginPage /></GuestRoute>} />
          <Route path="/login" element={<GuestRoute><LoginPage /></GuestRoute>} />
          <Route path="/forgot-password" element={<GuestRoute><ForgotPasswordPage /></GuestRoute>} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Troca de senha obrigatória — só ProtectedRoute (sem PermissionRoute):
              precisa funcionar independente das permissões do usuário. */}
          <Route
            path="/force-password-change"
            element={<ProtectedRoute><ForcePasswordChangePage /></ProtectedRoute>}
          />

          {/* App pages (protected + permission-gated) */}
          <Route path="/app" element={wrap(Dashboard, 'dashboard:read')} />
          <Route path="/app/unconfigured" element={wrap(Unconfigured, 'onus:read')} />
          <Route path="/app/configured" element={wrap(Configured, 'onus:read')} />
          <Route path="/app/onu/:serial/dashboard" element={wrap(OnuDashboardPage, 'onus:read')} />
          <Route path="/app/onu/:serial" element={wrap(OnuPage, 'onus:read')} />
          <Route path="/app/graphs" element={wrap(Graphs, 'dashboard:read')} />
          <Route path="/app/events" element={wrap(EventsPage, 'alerts:read')} />
          <Route path="/app/diagnostics" element={wrap(Diagnostics, 'alerts:read')} />
          <Route path="/app/tasks" element={wrap(Tasks, 'tasks:read')} />
          <Route path="/app/tasks/auto/:id" element={wrap(AutoTaskMonitorPage, 'auto_actions:read')} />
          <Route path="/app/bulk-actions" element={wrap(BulkActions, 'onus:write')} />
          <Route path="/app/external-changes" element={wrap(ExternalChanges, 'olts:read')} />
          <Route path="/app/reports" element={wrap(ReportsHomePage, 'reports:read')} />
          <Route path="/app/reports/inventory" element={wrap(ReportsInventoryPage, 'onus:read')} />
          <Route path="/app/reports/signal-quality" element={wrap(ReportsSignalQualityPage, 'onus:read')} />
          <Route path="/app/reports/traffic-summary" element={wrap(ReportsTrafficSummaryPage, 'onus:read')} />
          <Route path="/app/reports/pon-outages" element={wrap(ReportsPonOutagesPage, 'dashboard:read')} />
          <Route path="/app/reports/olt-overview" element={wrap(ReportsOltOverviewPage, 'dashboard:read')} />
          <Route path="/app/reports/poll-history" element={wrap(ReportsPollHistoryPage, 'olts:read')} />
          <Route path="/app/reports/map" element={wrap(ReportsNetworkMapPage, 'reports:read')} />
          <Route path="/app/reports/cto-occupancy" element={wrap(ReportsCTOOccupancyPage, 'reports:read')} />
          <Route path="/app/reports/topology" element={wrap(ReportsTopologyPage, 'reports:read')} />
          <Route path="/app/reports/pon-capacity" element={wrap(ReportsPonCapacityPage, 'reports:read')} />
          <Route path="/app/reports/pon-diagram" element={wrap(ReportsPonDiagramPage, 'reports:read')} />
          <Route path="/app/reports/onu-models" element={wrap(ReportsOnuModelsPage, 'reports:read')} />
          <Route path="/app/reports/speed-plans" element={wrap(ReportsSpeedPlansPage, 'reports:read')} />
          <Route path="/app/reports/onu-swaps" element={wrap(ReportsOnuSwapsPage, 'reports:read')} />
          <Route path="/app/reports/hardware-health" element={wrap(ReportsHardwareHealthPage, 'reports:read')} />
          <Route path="/app/reports/uptime" element={wrap(ReportsUptimePage, 'reports:read')} />
          <Route path="/app/reports/customer-consumption" element={wrap(ReportsCustomerConsumptionPage, 'billing:read')} />
          <Route path="/app/reports/rbac" element={wrap(ReportsRbacPage, 'users:read')} />
          <Route path="/app/reports/bulk-jobs" element={wrap(ReportsBulkJobsPage, 'reports:read')} />
          <Route path="/app/reports/backups" element={wrap(ReportsBackupsPage, 'reports:read')} />
          <Route path="/app/reports/mcp-usage" element={wrap(ReportsMcpUsagePage, 'integrations:read')} />
          <Route path="/app/reports/cli-audit/:id?" element={wrap(ReportsCliAuditPage, 'olt_cli_audit:read')} />
          <Route path="/app/reports/billing-olts" element={wrap(ReportsBillingOltsPage, 'billing:read')} />
          <Route path="/app/reports/payments" element={wrap(ReportsPaymentsPage, 'billing:read')} />
          <Route path="/app/settings" element={wrap(Settings, 'settings:read')} />
          <Route path="/app/settings/olts/:oltId" element={wrap(OltDetailPage, 'olts:read')} />
          <Route path="/app/settings/olts/:oltId/pon/:port" element={wrap(PonDetailPage, 'olts:read')} />
          <Route path="/app/settings/olts/:oltId/vlans/:vlanId/edit" element={wrap(VlanEditPage, 'olts:write')} />
          <Route path="/app/settings/:section" element={wrap(Settings, 'settings:read')} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster theme="dark" position="bottom-right" />
    </AppProvider>
  );
}

export default App;
